// src/ui/components/list/BlocksList.tsx
import React, { useEffect, useMemo, useState } from 'react';
/*import { Block } from '../../../../core/models/Block';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import type SwiperCore from 'swiper'; */
import 'swiper/css';
import 'swiper/css/effect-fade';
//import IndicatorDots from '../../utils/IndicatorDots';
import BlockItem from './BlockItem';
import { useDataContext } from '../../../../core/context/DataContext';
import { LiveSession } from '../../../../core/hooks/useLiveSession';
import { filterBlocks, filterBlocksByType } from '../../../../core/utils/filters';
import CardAnimated from '../../cards/CardAnimated';
import { fetchCategoryWods } from '../../../../core/services/wodsService';
import { Wod } from '../../../../core/models/Wod';
import { useWodFiltering } from '../../../../core/hooks/useWodFiltering';

interface BlocksListProps {
  wod: Wod[];
  liveSession: LiveSession | null;
  showSpecs?: boolean;
  showMedia?: boolean;
  onEmpty?: () => void;
  boxId?: number;
  filterToSingleWod?: number; // Si se especifica, solo mostrar bloques de este WOD
}

const BlocksList: React.FC<BlocksListProps> = ({
  wod,
  liveSession,
  showSpecs = true,
  showMedia = false,
  onEmpty,
  filterToSingleWod,
  boxId = 2, // moc
}) => {
  // --- hooks / estado (todos juntos al principio)
  const { blocks, loading, error } = useDataContext();
  //const [activeSlide, setActiveSlide] = useState(0);
  const expanded = liveSession?.expanded;
  const fullscreen = liveSession?.fullscreen;

  // estado categorias
  const [categoryState, setDefaultCats] = useState<any[]>([]);
  const [catsLoading, setCatsLoading] = useState(false);

  // Usar el hook centralizado para filtrar WODs
  // Si filterToSingleWod está especificado, incluir competidores para FullscreenDialog
  const { filteredWods } = useWodFiltering(wod, boxId, {
    includeCompetitors: !!filterToSingleWod, // Incluir competidores solo cuando se filtra a un WOD específico
    requireContent: false, // No requiere contenido específico
  });

  // Si se especifica filterToSingleWod, filtrar solo ese WOD
  const finalFilteredWods = filterToSingleWod
    ? filteredWods.filter((w) => w.id === filterToSingleWod)
    : filteredWods;

  // cargar lista de categories disponibles
  useEffect(() => {
    let mounted = true;
    async function loadCats() {
      setCatsLoading(true);
      try {
        const listCategoryWods = await fetchCategoryWods(Number(boxId));
        if (!mounted) return;
        setDefaultCats(Array.isArray(listCategoryWods) ? listCategoryWods : []);
      } catch (e) {
        console.warn('No se pudieron cargar category wods', e);
        if (mounted) setDefaultCats([]);
      } finally {
        if (mounted) setCatsLoading(false);
      }
    }
    loadCats();
    return () => {
      mounted = false;
    };
  }, [boxId]);

  // agrupar WODs por type_id pero usando finalFilteredWods
  const wodsByType = useMemo<Record<number, Wod[]>>(() => {
    const out: Record<number, Wod[]> = {};
    if (!Array.isArray(finalFilteredWods) || finalFilteredWods.length === 0) return out;

    for (const w of finalFilteredWods) {
      const t = Number(w.type_id ?? 0);
      if (!out[t]) out[t] = [];
      out[t].push(w);
    }

    return out;
  }, [finalFilteredWods]);

  // Verificar si hay bloques en los WODs filtrados (debe estar antes de los early returns)
  const hasBlocksInWods = useMemo(() => {
    return finalFilteredWods.some((w) => Array.isArray(w.blocks) && w.blocks.length > 0);
  }, [finalFilteredWods]);

  // aviso si no hay bloques
  useEffect(() => {
    if (!hasBlocksInWods && onEmpty) onEmpty();
  }, [hasBlocksInWods, onEmpty]);

  // decide si usar carousel (no es un hook, pero lo calculamos aquí)
  //const useCarousel = false; // manteniendo tu comportamiento

  // slides agrupados (2 por slide) --- no es hook pero lo calculamos antes del return
  /* const slideCount = Math.max(1, Math.ceil(blocks.length / 2));
  const slides: Block[][] = Array.from({ length: slideCount }, (_, idx) => {
    const first = blocks[2 * idx];
    const second = blocks[2 * idx + 1];
    return second ? [first, second] : [first];
  }); */

  // --- early returns (ahora TODOS los hooks/useMemo ya fueron declarados)
  if (loading) return <div>Cargando bloques…</div>;
  if (error) return <div>Error al cargar bloques: {error}</div>;
  if (!hasBlocksInWods) return <div>No hay bloques para este día.</div>;

  //const temp = filterBlocks(blocks);
  // --- render

  return (
    <>
      {hasBlocksInWods && (
        <div className="col-span-1 space-y-0">
          {catsLoading ? (
            <div>Cargando categorías...</div>
          ) : (
            <>
              {Array.isArray(categoryState) && categoryState.length > 0 ? (
                categoryState.map((cat) => {
                  // obtener WODs correspondientes a esta categoría (type_id === cat.id)
                  const wodsForCat: Wod[] = wodsByType[Number(cat.id)] ?? [];

                  // si no hay WODs para esta categoría, no renderizar nada
                  if (!wodsForCat || wodsForCat.length === 0) {
                    return null;
                  }

                  // si hay WODs, renderizar título (cat.name) y luego los bloques de cada WOD
                  return (
                    <div key={cat.id} className="mb-6">
                      {wodsForCat.map((wodItem) => {
                        // extraer y filtrar blocks del wod actual
                        const blocksForWod = Array.isArray(wodItem.blocks)
                          ? filterBlocks(wodItem.blocks)
                          : [];

                        // 🔍 DEBUG: Log de bloques antes y después de filtrar
                        console.log('[BlockList DEBUG] Bloques del WOD:', {
                          wodId: wodItem.id,
                          wodTitle: wodItem.title,
                          bloquesAntesDeFiltrar: Array.isArray(wodItem.blocks) ? wodItem.blocks.length : 0,
                          bloquesDespuesDeFiltrar: blocksForWod.length,
                          bloquesDetalle: Array.isArray(wodItem.blocks)
                            ? wodItem.blocks.map((b: any) => ({
                                id: b.id,
                                title: b.title,
                                work: b.work ? (b.work.length > 50 ? b.work.substring(0, 50) + '...' : b.work) : 'VACÍO',
                              }))
                            : [],
                          bloquesFiltradosDetalle: blocksForWod.map((b: any) => ({
                            id: b.id,
                            title: b.title,
                            work: b.work ? (b.work.length > 50 ? b.work.substring(0, 50) + '...' : b.work) : 'VACÍO',
                          })),
                        });

                        if (!blocksForWod || blocksForWod.length === 0) {
                          return (
                            <div key={`wod-${wodItem.id}`} className="mb-4">
                              <p className="text-sm text-gray-400">
                                WOD {wodItem.title} no tiene bloques visibles.
                              </p>
                            </div>
                          );
                        }

                        // dentro de wodsForCat.map(...)
                        return (
                          <div key={`wod-${wodItem.id}`} className="space-y-4 mb-0">
                            {blocksForWod.map((block) => (
                              <div key={block.id} className="mb-4">
                                <CardAnimated>
                                  <BlockItem
                                    block={block}
                                    showSpecs={showSpecs}
                                    activeBlockId={liveSession?.elementId}
                                    showMedia={showMedia}
                                    showExpanded={expanded && liveSession?.elementId === block.id}
                                    showFullScreen={
                                      fullscreen && liveSession?.elementId === block.id
                                    }
                                  />
                                </CardAnimated>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              ) : (
                // fallback si no hay categories: mostrar por tipo "wod"
                <>
                  {filterBlocksByType(filterBlocks(blocks), 'wod').map((block) => (
                    <CardAnimated key={block.id}>
                      <BlockItem
                        block={block}
                        showSpecs={showSpecs}
                        activeBlockId={liveSession?.elementId}
                        showMedia={showMedia}
                        showExpanded={expanded && liveSession?.elementId === block.id}
                        showFullScreen={fullscreen && liveSession?.elementId === block.id}
                      />
                    </CardAnimated>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default React.memo(BlocksList);
